import { useEffect, useState } from "react";
import { useAppDispatch } from "@redux";
import { ErrorToast } from "./toasts/ErrorToast";
import { SignInProgressCover } from "./Common/SignInProgressCover";
import { completeSocialSignIn } from "src/Module/Checkout/completeSocialSignIn";
import { readPendingSocialAction } from "src/Module/Checkout/pendingSocialAction";
import { fetchGoogleProfileNames } from "src/Utilities/googleProfile";
import { shouldCoverSocialHandback } from "src/Module/Checkout/socialSignInCover";
import { IGoogleLogin } from "@interfaces";

/**
 * Completes a Google sign-in that began from a page with no handler of its own.
 *
 * The in-page handlers only exist where the button is mounted (the sign-in
 * sheet) or where `useGoogleOAuth` is used (cart and book-now). A sign-in begun
 * from a tour page returns to a page with neither, so the token sat unread in
 * the fragment: the visitor came back apparently signed out, with an empty cart
 * and the ticket they had chosen never added.
 *
 * Scoped to exactly that case by the parked action: this only acts when a panel
 * recorded an intent before leaving, so the cart and book-now pages keep using
 * their own handling and nothing is processed twice.
 */
export const GoogleOAuthHandler = () => {
    const dispatch = useAppDispatch();

    const [isCompleting, setIsCompleting] = useState(() => {
        try {
            const hasToken = window.location.hash.includes("access_token");
            return shouldCoverSocialHandback(hasToken, readPendingSocialAction());
        } catch {
            return false;
        }
    });

    useEffect(() => {
        if (!isCompleting) return;

        const finish = async () => {
            try {
                const params = new URLSearchParams(window.location.hash.substring(1));
                const googleToken = params.get("access_token");
                if (!googleToken) {
                    setIsCompleting(false);
                    return;
                }

                const { googleLoginAction, setJwt, initializeAccountDetail } =
                    await import("@redux");
                const values: IGoogleLogin = { google_access_token: googleToken };
                const res: any = await dispatch(googleLoginAction(values));

                if (!res?.payload?.data?.accessToken) {
                    setIsCompleting(false);
                    ErrorToast({ Message: "Google sign-in failed. Please try again." });
                    return;
                }

                const { lsJwt } = await import("src/Utilities/storage");
                lsJwt.setItem(res.payload.data);
                dispatch(setJwt(res.payload.data));
                await dispatch(initializeAccountDetail());

                const destination = await completeSocialSignIn({
                    dispatch,
                    accessToken: res.payload.data.accessToken,
                    provider: "Google",
                    // Google's own token, not the app's: the profile lookup is on
                    // Google's host and the account it describes is theirs.
                    resolveSignupNames: () => fetchGoogleProfileNames(googleToken),
                });

                if (destination) {
                    const url = new URL(destination, window.location.origin);
                    window.location.href = `${url.pathname}${url.search}`;
                    return;
                }

                // Nowhere to go: drop the spent token so a remount cannot post it
                // again, and uncover the page.
                window.history.replaceState(
                    {},
                    document.title,
                    window.location.pathname + window.location.search,
                );
                setIsCompleting(false);
            } catch (error) {
                console.error("Google sign-in error:", error);
                setIsCompleting(false);
                ErrorToast({ Message: "Google sign-in failed. Please try again." });
            }
        };

        finish();
    }, [dispatch, isCompleting]);

    if (!isCompleting) return null;

    return <SignInProgressCover />;
};
